"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ProgrammeEntry } from "@/lib/core/types";
import { logActivity } from "@/lib/client-activity-log";
import { appGetItem, appSetItem, appRemoveItem } from "@/lib/auth-storage";
import { authedFetch } from "@/lib/api-client";

interface FavouriteProgramme {
  programme: ProgrammeEntry;
  university: string;
  addedAt: Date;
}

interface FavouritesContextType {
  favourites: FavouriteProgramme[];
  addFavourite: (programme: ProgrammeEntry, university: string) => void;
  removeFavourite: (programmeId: string) => void;
  isFavourite: (programmeId: string) => boolean;
  clearFavourites: (localOnly?: boolean) => Promise<void>;
}

const FavouritesContext = createContext<FavouritesContextType | undefined>(undefined);

// Helper function to refresh programme data from latest source
const refreshProgrammeData = (
  fav: FavouriteProgramme,
  allUniversities: Record<string, ProgrammeEntry[]>
): FavouriteProgramme => {
  try {
    const universityData = allUniversities[fav.university as keyof typeof allUniversities];
    if (universityData) {
      const latestProgramme = universityData.find(
        (p) => p.programmeDetails.id === fav.programme.programmeDetails.id
      );
      if (latestProgramme) {
        return {
          ...fav,
          programme: latestProgramme
        };
      }
    }
  } catch (e) {
    console.warn("Failed to refresh programme data:", e);
  }
  return fav;
};

export function FavouritesProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, status } = useAuth();
  const [favourites, setFavourites] = useState<FavouriteProgramme[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const isSavingRef = React.useRef(false);
  // Favourites may only be pushed to the account once we know what the account
  // already holds. Without this the sign-in render saves the empty list the
  // signed-out session was left with and wipes the stored favourites.
  const isDbHydratedRef = React.useRef(false);
  // Latest favourites that have not yet reached the database, flushed on unload
  // so a reload within the debounce window does not lose the last change.
  const unsavedRef = React.useRef<FavouriteProgramme[] | null>(null);
  const pendingSaveRef = React.useRef<FavouriteProgramme[] | null>(null);

  const loadLatestProgrammeMap = async () => {
    const adapter = await import("../calculator/adapter");
    return adapter.allUniversitiesFromPrograms as Record<string, ProgrammeEntry[]>;
  };

  // Save to database helper function
  const saveToDatabase = useCallback(async (favs: FavouriteProgramme[]) => {
    if (status !== "authenticated" || !authUser) return;

    // A save already in flight must not make this one disappear — park it and
    // let the running loop pick it up, otherwise the newest list is dropped.
    pendingSaveRef.current = favs;
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    try {
      while (pendingSaveRef.current) {
        const next = pendingSaveRef.current;
        pendingSaveRef.current = null;

        const response = await authedFetch("/api/user-data", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ favourites: next }),
        });

        if (!response.ok) {
          console.error(`Failed to save favourites: ${response.status}`);
          break;
        }
        if (unsavedRef.current === next) {
          unsavedRef.current = null;
        }
      }
    } catch (error) {
      console.error("Failed to save favourites to database:", error);
    } finally {
      isSavingRef.current = false;
    }
  }, [authUser, status]);

  // Initial load from storage after mount (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const loadStoredFavourites = async () => {
      const stored = appGetItem("jupas-favourites");
      if (!stored) {
        if (!cancelled) setIsLoaded(true);
        return;
      }

      try {
        const parsed = JSON.parse(stored) as FavouriteProgramme[];
        const allUniversities = await loadLatestProgrammeMap();
        const refreshed = parsed.map((f) => refreshProgrammeData({
          ...f,
          addedAt: new Date(f.addedAt)
        }, allUniversities));

        if (!cancelled) {
          setFavourites(refreshed);
          appSetItem("jupas-favourites", JSON.stringify(refreshed));
        }
      } catch (e) {
        console.error("Failed to load favourites:", e);
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    };

    loadStoredFavourites();

    return () => {
      cancelled = true;
    };
  }, []);

  // Load from MongoDB when authenticated (async)
  useEffect(() => {
    const loadFromDatabase = async () => {
      if (status === "loading") return;

      // Until this run finishes we do not know what the account holds, so the
      // auto-save effect must not push the current (possibly empty) list.
      isDbHydratedRef.current = false;

      if (status === "authenticated" && authUser) {
        try {
          const response = await authedFetch("/api/user-data");
          if (response.ok) {
            const data = await response.json() as { favourites?: FavouriteProgramme[] };
            if (data.favourites && Array.isArray(data.favourites)) {
              const allUniversities = await loadLatestProgrammeMap();
              const parsedFavourites = data.favourites.map((f) => refreshProgrammeData({
                ...f,
                addedAt: new Date(f.addedAt)
              }, allUniversities));
              setFavourites(parsedFavourites);
              appSetItem("jupas-favourites", JSON.stringify(parsedFavourites));
            } else {
              const stored = appGetItem("jupas-favourites");
              if (stored) {
                const parsed = JSON.parse(stored) as FavouriteProgramme[];
                const allUniversities = await loadLatestProgrammeMap();
                const parsedFavourites = parsed.map((f) => refreshProgrammeData({
                  ...f,
                  addedAt: new Date(f.addedAt)
                }, allUniversities));
                saveToDatabase(parsedFavourites);
              }
            }
          } else if (response.status !== 401) {
            console.error(`Failed to load favourites from database: ${response.status}`);
          }
        } catch (error) {
          console.error("Failed to load favourites from database:", error);
        }
      } else {
        // Signing out only drops the local copy; the account keeps its
        // favourites for the next sign-in.
        unsavedRef.current = null;
        setFavourites([]);
        appRemoveItem("jupas-favourites");
      }

      isDbHydratedRef.current = true;
    };

    if (isLoaded) {
      void loadFromDatabase();
    }
  }, [isLoaded, saveToDatabase, authUser, status]);

  // Auto-save to storage whenever favourites change
  useEffect(() => {
    if (!isLoaded) return;

    appSetItem("jupas-favourites", JSON.stringify(favourites));

    if (status !== "authenticated" || !authUser || !isDbHydratedRef.current) {
      return;
    }

    unsavedRef.current = favourites;
    const timeoutId = setTimeout(() => {
      void saveToDatabase(favourites);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [favourites, isLoaded, saveToDatabase, authUser, status]);

  // A reload or tab close inside the debounce window would otherwise drop the
  // last change, and the next sign-in would load the stale server copy over it.
  useEffect(() => {
    const flush = () => {
      const pending = unsavedRef.current;
      if (!pending || status !== "authenticated" || !authUser) return;
      unsavedRef.current = null;
      void authedFetch("/api/user-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ favourites: pending }),
        keepalive: true,
      }).catch(() => { /* page is going away; nothing useful to report */ });
    };

    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [authUser, status]);

  const addFavourite = useCallback((programme: ProgrammeEntry, university: string) => {
    // Requirement 3: favourites belong to a signed-in account. The UI asks for
    // sign-in before getting here; this keeps an anonymous session from
    // collecting favourites by any other route.
    if (status !== "authenticated") return;

    // Check if already exists
    if (favourites.some((f) => f.programme.programmeDetails.id === programme.programmeDetails.id)) {
      return;
    }
    const newFavourite = { programme, university, addedAt: new Date() };
    setFavourites((prev) => [...prev, newFavourite]);

    logActivity(
      "favourite-added",
      `Added programme to favourites: ${programme.programmeDetails.name_eng || programme.programmeDetails.id} (${university})`,
      { programmeId: programme.programmeDetails.id, university }
    );
  }, [favourites, status]);

  const removeFavourite = useCallback((programmeId: string) => {
    const existing = favourites.find(
      (f) => f.programme.programmeDetails.id === programmeId
    );
    setFavourites((prev) =>
      prev.filter((f) => f.programme.programmeDetails.id !== programmeId)
    );

    logActivity(
      "favourite-removed",
      `Removed programme from favourites: ${existing?.programme.programmeDetails.name_eng || programmeId}`,
      { programmeId }
    );
  }, [favourites]);

  const isFavourite = useCallback((programmeId: string) => {
    return favourites.some((f) => f.programme.programmeDetails.id === programmeId);
  }, [favourites]);

  const clearFavourites = async (localOnly = false) => {
    const clearedCount = favourites.length;
    setFavourites([]);
    appRemoveItem("jupas-favourites");

    logActivity(
      "favourites-cleared",
      localOnly
        ? `Cleared ${clearedCount} favourite programme(s) (this device only)`
        : `Cleared ${clearedCount} favourite programme(s)`,
      { count: clearedCount, localOnly }
    );

    if (!localOnly && status === "authenticated" && authUser) {
      await saveToDatabase([]);
    }
  };

  return (
    <FavouritesContext.Provider
      value={{ favourites, addFavourite, removeFavourite, isFavourite, clearFavourites }}
    >
      {children}
    </FavouritesContext.Provider>
  );
}

export function useFavourites() {
  const context = useContext(FavouritesContext);
  if (context === undefined) {
    throw new Error("useFavourites must be used within a FavouritesProvider");
  }
  return context;
}
